import { Component, Injector, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Model } from '../../../api/model';
import { ListPluginOptions } from '../../../plugins/abstract-plugins/list/interfaces/list-plugin-options.interface';
import { ActivityService } from '../../../activities/services/activity.service';
import { ActivityModel } from '../../../activities/models/activity.model';
import moment from 'moment-es6';
import { LoaderService } from '../../../support/loading/loader.service';
import { ToastService } from '../../../toast/services/toast.service';

@Component({
    selector: 'chassis-core-activity-plugin',
    templateUrl: './activity.plugin.html',
    styleUrls: ['./activity.plugin.scss'],
    providers: [ActivityService]
})
export class ActivityPlugin implements OnInit {
    /**
     * Observable containing the page's model instance.
     *
     * @type {Subject<Model>}
     */
    @Input() public modelSubject: Subject<Model>;

    /**
     * The options
     *
     * @type {ListPluginOptions}
     */
    public options: ListPluginOptions;

    /**
     * The activities
     *
     * @type {array<string, array<ActivityModel>>}
     */
    public activities: Array<{ period: string, activities: Array<ActivityModel> }> = [];

    /**
     * The current page
     *
     * @type {number}
     */
    public page = 1;

    /**
     * The amount of items per page
     *
     * @type {number}
     */
    public per_page = 10;

    /**
     * Indicates if there is an error
     *
     * @type {boolean}
     */
    public error = false;

    /**
     * Indicates whether you are loading or not
     *
     * @type {boolean}
     */
    public loading = true;

    /**
     * Whether there are more activities to load or not
     *
     * @type {boolean}
     */
    public hasNextPage = false;

    /**
     * Constructor
     *
     * @param injector
     * @param activityService
     * @param loaderService
     * @param toastService
     * @returns void
     */
    constructor(private injector: Injector, protected activityService: ActivityService, public loaderService: LoaderService, protected toastService: ToastService) {
    }

    /**
     * Init
     *
     * @returns void
     */
    ngOnInit() {
        this.loaderService.create('activity-loader').start();

        this.modelSubject.subscribe(model => {
            if (model.exists()) {
                this.activityService.defaultParams = {
                    entity: model._service.restEndpoint,
                    entityId: model.id
                };

                this.loadActivities();
            } else {
                this.loading = false;
                this.loaderService.get('activity-loader').stop();
            }
        });
    }

    /**
     * Load the activities
     *
     * @returns void
     */
    public loadActivities() {
        if (!this.loaderService.get('activity-loader').isLoading()) {
            this.loaderService.get('activity-loader').start();
        }

        this.activityService
            .paginate(this.page, this.per_page, {
                orderBy: 'id',
                sortedBy: 'desc'
            })
            .subscribe((activities) => {
                this.addActivities(activities);

                this.page++;

                this.hasNextPage = activities.count() === this.per_page;

                this.loading = false;
                this.loaderService.get('activity-loader').stop();
            }, error => {
                this.toastService.error('error', 'Could not retrieve activities');

                this.error = true;

                this.loading = false;
                this.loaderService.get('activity-loader').stop();
            }, () => {
                this.loading = false;
                this.loaderService.get('activity-loader').stop();
            });
    }

    /**
     * Get the number of changes
     *
     * @param activity
     * @returns number
     */
    public getNumberOfChanges(activity) {
        return Object.keys(activity.changes.attributes).length;
    }

    /**
     * Add the activities
     *
     * @param activities
     * @returns void
     */
    protected addActivities(activities) {
        activities.forEach(activity => {
            const period     = this.getActivityPeriod(activity.created_at);
            activity.changes = this.parseChanges(activity.changes);

            const group = this.activities.find(activityGroup => activityGroup.period === period);

            if (group) {
                group.activities.push(activity);
            } else {
                this.activities.push({
                    period: period,
                    activities: [activity]
                });
            }
        });
    }

    /**
     * Get the activity period
     *
     * @param created_at
     * @returns string
     */
    protected getActivityPeriod(created_at) {
        const date      = moment(created_at);
        const daysAgo   = moment().diff(date, 'days');
        const weeksAgo  = moment().diff(date, 'weeks');
        const monthsAgo = moment().diff(date, 'months');
        const yearsAgo  = moment().diff(date, 'years');

        if (daysAgo < 1) {
            return 'Today';
        }
        else if (daysAgo <= 7) {
            return daysAgo + ' days ago';
        }
        else if (weeksAgo > 0 && monthsAgo < 1) {
            return weeksAgo + ' weeks ago';
        }
        else if (monthsAgo > 0 && yearsAgo < 1) {
            return monthsAgo + ' months ago';
        }
        else if (yearsAgo > 0) {
            return yearsAgo + ' years ago';
        } else {
            return 'no period found?';
        }
    }

    /**
     * Parse the changes
     *
     * @param changes
     */
    private parseChanges(changes) {
        const parsedChanges = [];

        for (const field in changes.old) {
            if (changes.old.hasOwnProperty(field)) {
                parsedChanges.push({
                    field: field,
                    old: changes.old[field],
                    new: changes.attributes[field]
                });
            }
        }

        return parsedChanges;
    }
}


// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/activities/plugins/activity/activity.plugin.ts